import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaClock,
  FaInfoCircle,
  FaCheckCircle,
  FaTint,
  FaBoxOpen,
  FaCheck,
  FaTimes,
  FaCalendarCheck,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import config, { getUserCity } from "../../../config";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";
const FALLBACK_WATER_IMAGE =
  process.env.PUBLIC_URL + "/img/services/water/default_water.jpg";

const DrinkingWaterSupply = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const categoryKey = config.OUTDOOR_SERVICE_CATEGORY || "outdoor-services";
  const serviceKey = config.DRINKING_WATER_SUPPLY_SERVICE || "drinking-water-supply";

  const areaOptions = config.AREA_OPTIONS || {};
  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService =
    config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];

  const serviceLabel = selectedService?.label || "Drinking Water Supply";
  const categoryLabel = selectedCategory?.label || "Outdoor Services";

  const waterSupplyOptions =
    config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey]?.supplyTypes || [];

  const scheduleDaysLimit = config.SCHEDULE_DAYS_LIMIT ?? 3;
  const slotStartHour = config.SLOT_START_HOUR ?? 6;
  const slotEndHour = config.SLOT_END_HOUR ?? 20;
  const slotDurationHours = config.SLOT_DURATION_HOURS ?? 2;

  const [selectedSupplies, setSelectedSupplies] = useState([]);

  const [scheduleData, setScheduleData] = useState({
    name: "",
    date: "",
    time: "",
    slot: "",
    city: "",
    area: "",
    mobile: "",
    address: "",
    notes: "",
  });

  const getImageSrc = (imgPath) => {
    if (!imgPath) return FALLBACK_WATER_IMAGE;
    if (imgPath.startsWith("http")) return imgPath;
    return `${process.env.PUBLIC_URL}${imgPath}`;
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = FALLBACK_WATER_IMAGE;
  };

  const availableAreas = useMemo(() => {
    const selected =
      areaOptions[scheduleData.city] ||
      areaOptions[scheduleData.city?.toLowerCase()];

    if (Array.isArray(selected)) return selected;
    if (selected?.areas) return selected.areas;
    return [];
  }, [scheduleData.city, areaOptions]);

  const getSelectedSupply = (value) =>
    selectedSupplies.find((service) => service.value === value);

  const addSupply = (service) => {
    const exists = selectedSupplies.find((item) => item.value === service.value);

    if (exists) {
      setSelectedSupplies((prev) =>
        prev.map((item) =>
          item.value === service.value
            ? {
                ...item,
                quantity: Math.min(
                  (item.quantity || 0) + 1,
                  service.limit || 10000
                ),
              }
            : item
        )
      );
    } else {
      setSelectedSupplies((prev) => [
        ...prev,
        {
          ...service,
          quantity: service.min || 1,
          unit: service.unit || "unit",
          price: service.price || 0,
          limit: service.limit || 10000,
          min: service.min || 1,
        },
      ]);
    }
  };

  const updateQty = (value, delta, min, limit) => {
    setSelectedSupplies((prev) =>
      prev
        .map((item) => {
          if (item.value !== value) return item;

          const currentQty = item.quantity || 0;
          const nextQty = currentQty + delta;

          if (nextQty <= 0) return { ...item, quantity: 0 };

          const finalQty = Math.min(
            Math.max(nextQty, min || 1),
            limit || 10000
          );

          return { ...item, quantity: finalQty };
        })
        .filter((item) => item.quantity > 0)
    );
  };

  const total = useMemo(() => {
    return selectedSupplies.reduce(
      (sum, service) => sum + (service.price || 0) * (service.quantity || 0),
      0
    );
  }, [selectedSupplies]);

  const totalItems = useMemo(() => {
    return selectedSupplies.reduce(
      (sum, service) => sum + (service.quantity || 0),
      0
    );
  }, [selectedSupplies]);

  const formatHourLabel = (hour) => {
    const suffix = hour >= 12 ? "pm" : "am";
    const normalized = hour % 12 === 0 ? 12 : hour % 12;
    return `${normalized}${suffix}`;
  };

  const generateTimeSlots = () => {
    const slots = [];
    let count = 1;

    for (
      let start = slotStartHour;
      start + slotDurationHours <= slotEndHour;
      start += slotDurationHours
    ) {
      const end = start + slotDurationHours;
      slots.push({
        slot: `slot${count}`,
        label: `${formatHourLabel(start)}-${formatHourLabel(end)}`,
      });
      count += 1;
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleTimeSlotChange = (e) => {
    const selectedSlotKey = e.target.value;
    const selectedSlot = timeSlots.find((item) => item.slot === selectedSlotKey);

    setScheduleData((prev) => ({
      ...prev,
      slot: selectedSlot?.slot || "",
      time: selectedSlot?.label || "",
    }));
  };

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    const cleanValue =
      name === "mobile" ? value.replace(/\D/g, "").slice(0, 10) : value;

    setScheduleData((prev) => ({
      ...prev,
      [name]: cleanValue,
      ...(name === "city" ? { area: "" } : {}),
    }));
  };

  const handleScheduleWaterSupply = async (e) => {
    e.preventDefault();

    const { name, date, slot, city, area, mobile, address } = scheduleData;

    if (!name || !date || !slot || !city || !area || !mobile || !address) {
      alert("Please fill all required fields");
      return;
    }

    if (selectedSupplies.length === 0) {
      alert("Please select at least one water supply type");
      return;
    }

    const selectedDate = new Date(date);
    const today = new Date();
    const maxDate = new Date();

    maxDate.setDate(maxDate.getDate() + scheduleDaysLimit);
    today.setHours(0, 0, 0, 0);
    maxDate.setHours(23, 59, 59, 999);

    if (selectedDate < today || selectedDate > maxDate) {
      alert(
        `You can only schedule within today and next ${scheduleDaysLimit} days`
      );
      return;
    }

    try {
      setIsSubmitting(true);

const requestBody = {
  ...scheduleData,
  service_category: categoryKey,
  selected_city: getUserCity(),
  service: serviceKey,
  category_label: categoryLabel,
  service_label: serviceLabel,

  // Backend expects this field
  selected_services: selectedSupplies.map((service) => ({
    value: service.value,
    label: service.label,
    price: service.price,
    quantity: service.quantity,
    unit: service.unit || "unit",
    min: service.min || 1,
    line_total: service.price * service.quantity,
  })),

  total_items: totalItems,
  service_charge: total,
};

      const response = await fetch(`${BASE_URL}/api/schedule/labour/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to schedule water supply");
      }

      setShowThankYou(true);
      setSelectedSupplies([]);
      setScheduleData({
        name: "",
        date: "",
        time: "",
        slot: "",
        city: "",
        area: "",
        mobile: "",
        address: "",
        notes: "",
      });
    } catch (error) {
      alert(error.message || "Request failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => new Date().toISOString().split("T")[0];

  const getMaxDate = () => {
    const max = new Date();
    max.setDate(max.getDate() + scheduleDaysLimit);
    return max.toISOString().split("T")[0];
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background:
        "linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #eef2ff 100%)",
      padding: "30px 20px",
      fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
    },
    wrapper: {
      maxWidth: "1500px",
      width: "96%",
      margin: "0 auto",
    },
    hero: {
      background: "linear-gradient(135deg, #0f172a, #0284c7, #38bdf8)",
      color: "#fff",
      borderRadius: "28px",
      padding: "34px 36px",
      boxShadow: "0 20px 40px rgba(14, 116, 144, 0.22)",
      marginBottom: "24px",
    },
    heroBadge: {
      display: "inline-block",
      background: "rgba(255,255,255,0.14)",
      padding: "7px 14px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: "700",
      marginBottom: "12px",
    },
    heroTitle: {
      margin: 0,
      fontSize: "2.1rem",
      fontWeight: "800",
    },
    heroText: {
      marginTop: "10px",
      marginBottom: 0,
      color: "rgba(255,255,255,0.9)",
      maxWidth: "900px",
      lineHeight: 1.6,
      fontSize: "15px",
    },
    formCard: {
      background: "#fff",
      borderRadius: "28px",
      border: "1px solid #e5edf8",
      boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
      overflow: "hidden",
      width: "100%",
      maxWidth: "1320px",
      margin: "0 auto",
    },
    formHeader: {
      padding: "28px 40px",
      borderBottom: "1px solid #edf2f7",
      background: "linear-gradient(180deg, #ffffff, #f8fbff)",
    },
    formTitle: {
      margin: 0,
      fontSize: "1.6rem",
      fontWeight: "800",
      color: "#0f172a",
    },
    formSub: {
      margin: "8px 0 0 0",
      color: "#64748b",
      fontSize: "14px",
    },
    body: {
      padding: "32px 40px 36px",
    },
    section: {
      marginBottom: "28px",
      padding: "24px",
      borderRadius: "20px",
      background: "#f8fbff",
      border: "1px solid #e6eef8",
    },
    sectionTitle: {
      margin: "0 0 18px 0",
      fontSize: "1.1rem",
      fontWeight: "800",
      color: "#0f172a",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "20px",
      alignItems: "start",
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#334155",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      minHeight: "120px",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
      resize: "vertical",
    },
    submitWrap: {
      display: "flex",
      justifyContent: "center",
      marginTop: "30px",
    },
    submitBtn: {
      border: "none",
      borderRadius: "999px",
      background: "linear-gradient(135deg, #0284c7, #0ea5e9)",
      color: "#fff",
      padding: "15px 34px",
      fontSize: "15px",
      fontWeight: "800",
      minWidth: "260px",
      boxShadow: "0 12px 24px rgba(2, 132, 199, 0.24)",
      cursor: "pointer",
    },
    helper: {
      color: "#64748b",
      fontSize: "13px",
      margin: 0,
      lineHeight: 1.6,
    },
    supplyCard: {
      display: "flex",
      alignItems: "stretch",
      gap: "18px",
      background: "linear-gradient(180deg, #ffffff, #f8fbff)",
      border: "1px solid #dbe7f5",
      borderRadius: "24px",
      padding: "16px",
      marginTop: "18px",
      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.07)",
      transition: "all 0.25s ease",
      flexWrap: "wrap",
    },
    supplyLeft: {
      flex: "0 0 320px",
      maxWidth: "320px",
      minWidth: "260px",
    },
    supplyImage: {
      width: "100%",
      height: "100%",
      minHeight: "240px",
      maxHeight: "280px",
      objectFit: "cover",
      borderRadius: "18px",
      border: "1px solid #dbe7f5",
      background: "#e2e8f0",
      display: "block",
    },
    supplyRight: {
      flex: "1 1 520px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minWidth: "280px",
    },
    supplyTopRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
    },
    supplyTitle: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: "800",
      color: "#0f172a",
      lineHeight: 1.2,
    },
    supplyPriceTag: {
      display: "inline-block",
      marginTop: "10px",
      background: "linear-gradient(135deg, #e0f2fe, #f0f9ff)",
      color: "#0369a1",
      border: "1px solid #bae6fd",
      borderRadius: "999px",
      padding: "8px 14px",
      fontSize: "13px",
      fontWeight: "800",
    },
    metaGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "12px",
      marginTop: "18px",
    },
    metaCard: {
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "16px",
      padding: "12px 14px",
      boxShadow: "0 6px 12px rgba(15, 23, 42, 0.04)",
    },
    metaLabel: {
      fontSize: "12px",
      color: "#64748b",
      fontWeight: "700",
      marginBottom: "4px",
      textTransform: "uppercase",
      letterSpacing: "0.4px",
    },
    metaValue: {
      fontSize: "15px",
      color: "#0f172a",
      fontWeight: "800",
    },
    pointsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "16px",
      marginTop: "18px",
    },
    pointsCard: {
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "18px",
      padding: "16px",
      boxShadow: "0 6px 12px rgba(15, 23, 42, 0.04)",
    },
    pointsTitleInclude: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      fontWeight: "800",
      color: "#047857",
      marginBottom: "10px",
    },
    pointsTitleExclude: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      fontWeight: "800",
      color: "#b91c1c",
      marginBottom: "10px",
    },
    list: {
      margin: 0,
      paddingLeft: "18px",
      color: "#334155",
      fontSize: "14px",
      lineHeight: 1.7,
    },
    qtyWrap: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
      marginTop: "18px",
    },
    qtyBtn: {
      width: "42px",
      height: "42px",
      border: "none",
      borderRadius: "12px",
      background: "linear-gradient(135deg, #0284c7, #0ea5e9)",
      color: "#fff",
      fontSize: "18px",
      fontWeight: "800",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    qtyBtnDisabled: {
      width: "42px",
      height: "42px",
      border: "none",
      borderRadius: "12px",
      background: "#cbd5e1",
      color: "#64748b",
      fontSize: "18px",
      fontWeight: "800",
      cursor: "not-allowed",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    qtyValue: {
      minWidth: "60px",
      textAlign: "center",
      fontSize: "20px",
      fontWeight: "800",
      color: "#0f172a",
    },
    priceBox: {
      background: "linear-gradient(135deg, #ecfeff, #f0fdfa)",
      border: "1px solid #a5f3fc",
      borderRadius: "16px",
      padding: "16px 18px",
      marginTop: "20px",
      color: "#0f766e",
    },
    popupOverlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    },
    popupCard: {
      background: "#fff",
      borderRadius: "18px",
      padding: "22px 24px",
      maxWidth: "360px",
      width: "90%",
      textAlign: "center",
      boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
    },
popupTitle: {
  margin: "10px 0 6px 0",
  fontSize: "1.1rem",
  fontWeight: "800",
  color: "#1d4ed8",
},
    popupText: {
      margin: 0,
      fontSize: "14px",
      color: "#4b5563",
    },
popupButton: {
  marginTop: "16px",
  borderRadius: "999px",
  border: "none",
  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "700",
  padding: "10px 24px",
  cursor: "pointer",
  boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
},
  };

  return (
    <>
      {showThankYou && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <FaCheckCircle color="#16a34a" size={32} />
            <h3 style={styles.popupTitle}>Thank you!</h3>
            <p style={styles.popupText}>
              Your drinking water supply request has been submitted successfully.
            </p>
            <button
              type="button"
              style={styles.popupButton}
              onClick={() => {
                setShowThankYou(false);
                navigate("/");
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div style={styles.page}>
        <div style={styles.wrapper}>
          <div style={styles.hero}>
            <div style={styles.heroBadge}>{serviceLabel}</div>
            <h1 style={styles.heroTitle}>Book Drinking Water Supply</h1>
            <p style={styles.heroText}>
              Choose packaged drinking water bottle or can size, set quantity,
              and schedule delivery by date, time, city, and area.
            </p>
          </div>

          <form style={styles.formCard} onSubmit={handleScheduleWaterSupply}>
            <div style={styles.formHeader}>
              <h2 style={styles.formTitle}>Schedule Drinking Water Supply</h2>
              <p style={styles.formSub}>
                Select water supply type, quantity, and delivery details.
              </p>
            </div>

            <div style={styles.body}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  <FaTint />
                  Water Supply Options
                </h3>

                <p style={styles.helper}>
                  Select one or more bottle or can sizes and adjust quantity.
                </p>

                {waterSupplyOptions.map((item) => {
                  const selected = getSelectedSupply(item.value);
                  const qty = selected?.quantity || 0;
                  const minQty = item.min || 1;
                  const reachedMax = qty >= (item.limit || 10000);

                  return (
                    <div key={item.value} style={styles.supplyCard}>
                      <div style={styles.supplyLeft}>
                        <img
                          src={getImageSrc(item.image)}
                          alt={item.label}
                          style={styles.supplyImage}
                          onError={handleImageError}
                        />
                      </div>

                      <div style={styles.supplyRight}>
                        <div>
                          <div style={styles.supplyTopRow}>
                            <div>
                              <h4 style={styles.supplyTitle}>{item.label}</h4>
                              <div style={styles.supplyPriceTag}>
                                ₹{item.price} / {item.unit}
                              </div>
                            </div>
                          </div>

                          <div style={styles.metaGrid}>
                            <div style={styles.metaCard}>
                              <div style={styles.metaLabel}>Minimum</div>
                              <div style={styles.metaValue}>
                                {item.min || "N/A"} {item.unit}
                              </div>
                            </div>

                            <div style={styles.metaCard}>
                              <div style={styles.metaLabel}>Limit</div>
                              <div style={styles.metaValue}>
                                {item.limit || "N/A"} {item.unit}
                              </div>
                            </div>

                            <div style={styles.metaCard}>
                              <div style={styles.metaLabel}>Type</div>
                              <div style={styles.metaValue}>{item.value || "N/A"}</div>
                            </div>

                            <div style={styles.metaCard}>
                              <div style={styles.metaLabel}>Unit</div>
                              <div style={styles.metaValue}>{item.unit || "N/A"}</div>
                            </div>
                          </div>

                          <div style={styles.pointsGrid}>
                            <div style={styles.pointsCard}>
                              <div style={styles.pointsTitleInclude}>
                                <FaCheck />
                                Includes
                              </div>
                              <ul style={styles.list}>
                                {(item.includes || []).map((inc, idx) => (
                                  <li key={idx}>{inc}</li>
                                ))}
                              </ul>
                            </div>

                            <div style={styles.pointsCard}>
                              <div style={styles.pointsTitleExclude}>
                                <FaTimes />
                                Excludes
                              </div>
                              <ul style={styles.list}>
                                {(item.excludes || []).map((exc, idx) => (
                                  <li key={idx}>{exc}</li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <div style={styles.qtyWrap}>
                            <button
                              type="button"
                              style={qty === 0 ? styles.qtyBtnDisabled : styles.qtyBtn}
                              disabled={qty === 0}
                              onClick={() =>
                                updateQty(item.value, -(item.min || 1), minQty, item.limit || 10000)
                              }
                            >
                              <FaMinus />
                            </button>

                            <div style={styles.qtyValue}>{qty}</div>

                            <button
                              type="button"
                              style={reachedMax ? styles.qtyBtnDisabled : styles.qtyBtn}
                              disabled={reachedMax}
                              onClick={() => {
                                if (!selected) {
                                  addSupply(item);
                                } else {
                                  updateQty(
                                    item.value,
                                    item.min || 1,
                                    minQty,
                                    item.limit || 10000
                                  );
                                }
                              }}
                            >
                              <FaPlus />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {selectedSupplies.length > 0 ? (
                  <div style={styles.priceBox}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>
                      Booking Summary
                    </div>
                    {selectedSupplies.map((service) => (
                      <div key={service.value} style={{ marginBottom: 8, color: "#000" }}>
                        {service.label} - {service.quantity} x ₹{service.price} = ₹
                        {service.quantity * service.price}
                      </div>
                    ))}
                    <p style={{ fontSize: "1.5rem", fontWeight: 900, margin: 0 }}>
                      Total: ₹{total}
                    </p>
                  </div>
                ) : null}
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  <FaInfoCircle />
                  Delivery Details
                </h3>

                <div style={styles.grid}>
                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaUser /> Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={scheduleData.name}
                      onChange={handleScheduleChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaCalendarCheck /> Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      min={getMinDate()}
                      max={getMaxDate()}
                      value={scheduleData.date}
                      onChange={handleScheduleChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaClock /> Time Slot
                    </label>
                    <select
                      name="slot"
                      value={scheduleData.slot}
                      onChange={handleTimeSlotChange}
                      style={styles.select}
                      required
                    >
                      <option value="">Select Time Slot</option>
                      {timeSlots.map((item) => (
                        <option key={item.slot} value={item.slot}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaMapMarkerAlt /> City
                    </label>
                    <select
                      name="city"
                      value={scheduleData.city}
                      onChange={handleScheduleChange}
                      style={styles.select}
                      required
                    >
                      <option value="">Select City</option>
                      {Object.keys(areaOptions).map((city) => (
                        <option key={city} value={city}>
                          {city.charAt(0).toUpperCase() + city.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaMapMarkerAlt /> Area
                    </label>
                    <select
                      name="area"
                      value={scheduleData.area}
                      onChange={handleScheduleChange}
                      style={styles.select}
                      required
                      disabled={!scheduleData.city}
                    >
                      <option value="">Select Area</option>
                      {availableAreas.map((area) => (
                        <option key={area} value={area}>
                          {area}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaPhoneAlt /> Mobile
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={scheduleData.mobile}
                      onChange={handleScheduleChange}
                      pattern="[0-9]{10}"
                      minLength={10}
                      maxLength={10}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaMapMarkerAlt /> Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={scheduleData.address}
                      onChange={handleScheduleChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaBoxOpen /> Notes
                    </label>
                    <textarea
                      name="notes"
                      value={scheduleData.notes}
                      onChange={handleScheduleChange}
                      style={styles.textarea}
                      placeholder="Delivery instructions, event details, floor number, storage note, etc."
                    />
                  </div>
                </div>
              </div>

              <div style={styles.submitWrap}>
                <button
                  type="submit"
                  style={styles.submitBtn}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Scheduling..." : "Schedule Water Supply"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default DrinkingWaterSupply;